/**
 * CacheManager - Core caching operations for organization data
 * Extracted from OrganizationCache to follow Single Responsibility Principle
 */

import { CACHE_TTL } from '../config/cacheConfig';

export class CacheManager {
  constructor(storage) {
    this.storage = storage;
    this.TTL = CACHE_TTL;

    // Initialize cache structures
    const initialized = this.storage.loadFromStorage();
    if (initialized) {
      this.cache = initialized.cache;
      this.expiryTimes = initialized.expiryTimes;

      // Clean expired items on load
      const expiredCount = this.storage.cleanExpiredItems(this.cache, this.expiryTimes);
      if (expiredCount > 0) {
        this.storage.saveToStorage(this.cache, this.expiryTimes);
      }
    } else {
      const empty = this.storage.initializeEmptyCache();
      this.cache = empty.cache;
      this.expiryTimes = empty.expiryTimes;
    }

    // Initialize loading states
    this.loadingStates = {
      roles: {},
      members: {},
      divisions: {},
      groups: {},
      invitations: {},
      organization: {},
      notifications: {}
    };

    // Debounce save to reduce I/O
    this.saveDebounceTimeout = null;
    this.saveDebounceDelay = 300; // 300ms delay
  }

  /**
   * Generate cache key for organization data
   * @param {string} orgId - Organization ID
   * @param {string} dataType - Type of data (roles, members, etc.)
   * @param {string} subKey - Optional sub-key for nested data
   * @returns {string} Cache key
   */
  getCacheKey(orgId, dataType, subKey = '') {
    return subKey ? `${orgId}:${dataType}:${subKey}` : `${orgId}:${dataType}`;
  }

  /**
   * Get cached data
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {string} subKey - Optional sub-key
   * @returns {*} Cached data or null if not found/expired
   */
  get(dataType, orgId, subKey = '') {
    const key = this.getCacheKey(orgId, dataType, subKey);

    if (this.cache[dataType] && this.cache[dataType][key] && this.expiryTimes[dataType][key] > Date.now()) {
      return this.cache[dataType][key];
    }

    // Remove if expired
    if (this.cache[dataType] && this.cache[dataType][key]) {
      delete this.cache[dataType][key];
      delete this.expiryTimes[dataType][key];
      this.scheduleSave();
    }

    return null;
  }

  /**
   * Set cached data
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {*} data - Data to cache
   * @param {string} subKey - Optional sub-key
   */
  set(dataType, orgId, data, subKey = '') {
    if (!data) return;

    const key = this.getCacheKey(orgId, dataType, subKey);

    // Ensure the dataType exists in cache structure
    if (!this.cache[dataType]) {
      this.cache[dataType] = {};
    }
    if (!this.expiryTimes[dataType]) {
      this.expiryTimes[dataType] = {};
    }

    this.cache[dataType][key] = data;
    this.expiryTimes[dataType][key] = Date.now() + this.TTL[dataType];

    this.scheduleSave();
  }

  /**
   * Check if data is currently loading
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {string} subKey - Optional sub-key
   * @returns {boolean} True if loading
   */
  isLoading(dataType, orgId, subKey = '') {
    const key = this.getCacheKey(orgId, dataType, subKey);
    return this.loadingStates[dataType] && this.loadingStates[dataType][key] === true;
  }

  /**
   * Set loading state
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {boolean} isLoading - Loading state
   * @param {string} subKey - Optional sub-key
   */
  setLoading(dataType, orgId, isLoading = true, subKey = '') {
    const key = this.getCacheKey(orgId, dataType, subKey);

    // Ensure the dataType exists in loading states
    if (!this.loadingStates[dataType]) {
      this.loadingStates[dataType] = {};
    }

    if (isLoading) {
      this.loadingStates[dataType][key] = true;
    } else {
      delete this.loadingStates[dataType][key];
    }
  }

  /**
   * Invalidate specific cache entry
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {string} subKey - Optional sub-key
   * @returns {boolean} True if entry was invalidated
   */
  invalidate(dataType, orgId, subKey = '') {
    const key = this.getCacheKey(orgId, dataType, subKey);

    if (this.cache[dataType] && this.cache[dataType][key]) {
      delete this.cache[dataType][key];
      delete this.expiryTimes[dataType][key];
      if (this.loadingStates[dataType]) {
        delete this.loadingStates[dataType][key];
      }
      this.scheduleSave();
      return true;
    }

    return false;
  }

  /**
   * Invalidate all cache for an organization
   * @param {string} orgId - Organization ID
   * @returns {number} Number of entries invalidated
   */
  invalidateOrganization(orgId) {
    let totalInvalidated = 0;

    Object.keys(this.cache).forEach(dataType => {
      if (this.cache[dataType]) {
        Object.keys(this.cache[dataType]).forEach(key => {
          if (key.startsWith(orgId)) {
            delete this.cache[dataType][key];
            if (this.expiryTimes[dataType]) {
              delete this.expiryTimes[dataType][key];
            }
            if (this.loadingStates[dataType]) {
              delete this.loadingStates[dataType][key];
            }
            totalInvalidated++;
          }
        });
      }
    });

    if (totalInvalidated > 0) {
      this.scheduleSave();
    }

    return totalInvalidated;
  }

  /**
   * Clear all cache
   */
  clearAll() {
    const empty = this.storage.initializeEmptyCache();
    this.cache = empty.cache;
    this.expiryTimes = empty.expiryTimes;
    this.loadingStates = {
      roles: {},
      members: {},
      divisions: {},
      groups: {},
      invitations: {},
      organization: {},
      notifications: {}
    };
    this.storage.clearStorage();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const stats = {
      totalEntries: 0,
      entriesByType: {},
      oldestEntry: null,
      newestEntry: null
    };

    Object.keys(this.cache).forEach(dataType => {
      if (this.cache[dataType]) {
        const entries = Object.keys(this.cache[dataType]).length;
        stats.totalEntries += entries;
        stats.entriesByType[dataType] = entries;

        // Track oldest and newest entries
        Object.keys(this.expiryTimes[dataType] || {}).forEach(key => {
          const expiry = this.expiryTimes[dataType][key];
          if (!stats.oldestEntry || expiry < stats.oldestEntry) {
            stats.oldestEntry = expiry;
          }
          if (!stats.newestEntry || expiry > stats.newestEntry) {
            stats.newestEntry = expiry;
          }
        });
      }
    });

    return stats;
  }

  /**
   * Schedule a debounced save to localStorage
   * Reduces I/O operations by batching multiple rapid changes
   */
  scheduleSave() {
    if (this.saveDebounceTimeout) {
      clearTimeout(this.saveDebounceTimeout);
    }

    this.saveDebounceTimeout = setTimeout(() => {
      this.storage.saveToStorage(this.cache, this.expiryTimes);
      this.saveDebounceTimeout = null;
    }, this.saveDebounceDelay);
  }

  /**
   * Force immediate save to localStorage
   * Use before page unload or critical operations
   */
  forceSave() {
    if (this.saveDebounceTimeout) {
      clearTimeout(this.saveDebounceTimeout);
      this.saveDebounceTimeout = null;
    }
    this.storage.saveToStorage(this.cache, this.expiryTimes);
  }
}
